// ============================================================
//  src/pages/billing/BillingPage.js
//  Styles: ./BillingPage.css (scoped under .bil-*)
// ============================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { getBillingOverview, getBillingHistory, upgradePlan, listBots } from '../../api/client';
import './BillingPage.css';

const PAGE_SIZE = 10;
const STORAGE_KEY = 'pocketfi_pending_payment_id';

function fmtNaira(amount) {
  const n = Number(amount) || 0;
  return '₦' + n.toLocaleString('en-NG', { maximumFractionDigits: 0 });
}

function parseDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(String(dateStr).replace(' ', 'T'));
  return isNaN(d.getTime()) ? null : d;
}

function fmtDate(dateStr, withTime = false) {
  const d = parseDate(dateStr);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
}

// Remaining time on the current subscription
function getCountdown(expiresAt) {
  const end = parseDate(expiresAt);
  if (!end) return null;
  const diff = end.getTime() - Date.now();
  if (diff <= 0) return { expired: true, tone: 'danger', label: 'Expired', days: 0 };
  const days  = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins  = Math.floor((diff % 3600000) / 60000);
  const tone  = days > 7 ? 'ok' : days >= 3 ? 'warn' : 'danger';
  const label = days >= 1 ? `${days} day${days === 1 ? '' : 's'} left` : `${hours}h ${mins}m left`;
  return { expired: false, tone, label, days: diff / 86400000 };
}

const STATUS_META = {
  success:   { cls: 'is-success',   label: 'Paid'      },
  pending:   { cls: 'is-pending',   label: 'Pending'   },
  failed:    { cls: 'is-failed',    label: 'Failed'    },
  cancelled: { cls: 'is-cancelled', label: 'Cancelled' },
};

function StatusPill({ status }) {
  const s = STATUS_META[status] || STATUS_META.pending;
  return <span className={`bil-status ${s.cls}`}><span className="bil-status-dot" />{s.label}</span>;
}

// ── Pagination (compact, works for any number of pages) ──────
function Pagination({ page, total, pageSize, onChange }) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;

  const pages = [];
  const add = p => { if (!pages.includes(p)) pages.push(p); };
  add(1);
  for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) add(p);
  add(totalPages);
  const withGaps = [];
  pages.forEach((p, i) => {
    if (i > 0 && p - pages[i - 1] > 1) withGaps.push('gap-' + p);
    withGaps.push(p);
  });

  return (
    <div className="bil-pager">
      <span className="bil-pager-info">
        {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total}
      </span>
      <div className="bil-pager-btns">
        <button type="button" onClick={() => onChange(page - 1)} disabled={page === 1} aria-label="Previous page">
          <i className="fa-solid fa-chevron-left" />
        </button>
        {withGaps.map(p => typeof p === 'string'
          ? <span key={p} className="bil-pager-gap">…</span>
          : (
            <button key={p} type="button" className={p === page ? 'is-active' : ''} onClick={() => onChange(p)} aria-current={p === page ? 'page' : undefined}>
              {p}
            </button>
          ))}
        <button type="button" onClick={() => onChange(page + 1)} disabled={page === totalPages} aria-label="Next page">
          <i className="fa-solid fa-chevron-right" />
        </button>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="bil" aria-busy="true">
      <div className="bil-head"><div><span className="bil-sk w40" /><span className="bil-sk w60 h28" /></div></div>
      <div className="bil-hero bil-hero-sk"><span className="bil-sk w30 dark" /><span className="bil-sk w50 h28 dark" /><span className="bil-sk w70 dark" /></div>
      <div className="bil-plans">{[0, 1, 2].map(i => <div key={i} className="bil-plan"><span className="bil-sk w40" /><span className="bil-sk w60 h28" /><span className="bil-sk w80" /><span className="bil-sk w70" /></div>)}</div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────
export default function BillingPage() {
  const [overview,  setOverview]  = useState(null);
  const [history,   setHistory]   = useState([]);
  const [botCount,  setBotCount]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [page,      setPage]      = useState(1);
  const plansRef   = useRef(null);
  const historyRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [ov, hist] = await Promise.all([getBillingOverview(), getBillingHistory()]);
      setOverview(ov.data);
      setHistory(hist.data.payments || []);
    } catch {
      toast.error('Could not load billing details');
    } finally {
      setLoading(false);
    }
    // Bot usage is a nice-to-have — never block the page on it
    try {
      const b = await listBots();
      if (b.data?.success) setBotCount((b.data.bots ?? []).length);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleUpgrade(plan) {
    if (upgrading) return;
    setUpgrading(plan.plSlug);
    try {
      const res = await upgradePlan(plan.plSlug);
      if (res.data.requires_payment) {
        localStorage.setItem(STORAGE_KEY, res.data.payment_id);
        window.location.href = res.data.payment_link;
      } else {
        toast.success(res.data.message || 'Plan updated');
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not start upgrade');
    } finally {
      setUpgrading(null);
    }
  }

  function changePage(p) {
    setPage(p);
    historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  if (loading) return <Skeleton />;

  const current       = overview?.current_plan;
  const plans         = overview?.plans || [];
  const pocketfiReady = overview?.pocketfi_ready;

  const currentPrice = Number(current?.plPrice) || 0;
  const cd           = current?.subExpiresAt ? getCountdown(current.subExpiresAt) : null;
  const interval     = Number(current?.plInterval) || 0;
  const periodPct    = cd && interval ? Math.max(0, Math.min(100, (cd.days / interval) * 100)) : null;
  const maxBots      = current?.plMaxBots != null ? Number(current.plMaxBots) : null;
  const botPct       = maxBots && botCount != null ? Math.min(100, Math.round((botCount / maxBots) * 100)) : null;

  // Highlight the cheapest paid plan above the current one as "recommended"
  const sortedPaid   = plans.filter(p => Number(p.plPrice) > currentPrice).sort((a, b) => Number(a.plPrice) - Number(b.plPrice));
  const recommended  = sortedPaid[0]?.plSlug;

  const totalTx    = history.length;
  const paginated  = history.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPaid  = history.filter(p => p.payStatus === 'success').reduce((s, p) => s + (Number(p.payAmount) || 0), 0);
  const lastPaid   = history.find(p => p.payStatus === 'success');

  const priceLine = current
    ? currentPrice > 0
      ? <>{fmtNaira(currentPrice)}<span> / {current.plInterval ? `${current.plInterval} days` : 'one-time'}</span></>
      : <>Free<span>{current.plInterval ? ` · ${current.plInterval}-day access` : ' forever'}</span></>
    : null;

  return (
    <div className="bil">
      {/* Header */}
      <header className="bil-head">
        <div>
          <h1>Billing & plans</h1>
          <p>Manage your subscription, compare plans and keep track of every payment.</p>
        </div>
        <span className="bil-secure"><i className="fa-solid fa-shield-halved" /> Secure checkout via PocketFi</span>
      </header>

      {!pocketfiReady && (
        <div className="bil-alert" role="status">
          <span className="bil-alert-ic"><i className="fa-solid fa-plug-circle-exclamation" /></span>
          <div>
            <strong>Online payments aren’t available right now</strong>
            <p>Paid upgrades are temporarily paused. You can still switch to a free plan.</p>
          </div>
        </div>
      )}

      {cd && (cd.expired || cd.tone === 'danger') && (
        <div className={`bil-alert ${cd.expired ? 'is-danger' : 'is-warn'}`} role="alert">
          <span className="bil-alert-ic"><i className={`fa-solid ${cd.expired ? 'fa-circle-xmark' : 'fa-hourglass-half'}`} /></span>
          <div>
            <strong>{cd.expired ? `Your ${current.plName} plan has expired` : `Your ${current.plName} plan ends soon`}</strong>
            <p>{cd.expired ? 'Renew to keep all your bots running without interruption.' : `It expires on ${fmtDate(current.subExpiresAt)}. Renew before then so your bots never pause.`}</p>
          </div>
          <button type="button" className="bil-btn bil-btn-dark" onClick={() => plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            See plans <i className="fa-solid fa-arrow-down" />
          </button>
        </div>
      )}

      {/* Current plan */}
      <section className="bil-hero" aria-label="Current plan">
        <div className="bil-hero-glow" aria-hidden="true" />
        {current ? (
          <div className="bil-hero-grid">
            <div className="bil-hero-main">
              <span className="bil-hero-eyebrow"><i className="fa-solid fa-gem" /> Current plan</span>
              <h2>{current.plName}</h2>
              <div className="bil-hero-price">{priceLine}</div>
              {current.plFeatures?.length > 0 && (
                <ul className="bil-hero-feats">
                  {current.plFeatures.slice(0, 6).map((f, i) => <li key={i}><i className="fa-solid fa-check" /> {f}</li>)}
                </ul>
              )}
              <div className="bil-hero-actions">
                <button type="button" className="bil-btn bil-btn-brand" onClick={() => plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                  {sortedPaid.length ? 'Upgrade plan' : 'Compare plans'} <i className="fa-solid fa-arrow-down" />
                </button>
                {totalTx > 0 && (
                  <button type="button" className="bil-btn bil-btn-ondark" onClick={() => historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                    Payment history
                  </button>
                )}
              </div>
            </div>

            <div className="bil-hero-side">
              {cd ? (
                <div className="bil-meter-card">
                  <div className="bil-meter-top">
                    <span>Billing period</span>
                    <b className={`tone-${cd.tone}`}>{cd.label}</b>
                  </div>
                  {periodPct !== null && (
                    <div className={`bil-meter tone-${cd.tone}`}><span style={{ width: `${periodPct}%` }} /></div>
                  )}
                  <div className="bil-meter-foot">
                    {cd.expired ? 'Expired on' : 'Renews / expires on'} <b>{fmtDate(current.subExpiresAt)}</b>
                  </div>
                </div>
              ) : (
                <div className="bil-meter-card">
                  <div className="bil-meter-top"><span>Billing period</span><b className="tone-ok">No expiry</b></div>
                  <div className="bil-meter-foot">This plan doesn’t need renewing.</div>
                </div>
              )}

              {maxBots !== null && (
                <div className="bil-meter-card">
                  <div className="bil-meter-top">
                    <span>Bot slots</span>
                    <b>{botCount ?? '—'}<em> / {maxBots}</em></b>
                  </div>
                  {botPct !== null && <div className={`bil-meter ${botPct >= 100 ? 'tone-danger' : 'tone-violet'}`}><span style={{ width: `${botPct}%` }} /></div>}
                  <div className="bil-meter-foot">
                    {botCount == null ? 'Bots allowed on this plan' : botCount >= maxBots ? 'All slots used — upgrade for more' : `${maxBots - botCount} slot${maxBots - botCount === 1 ? '' : 's'} available`}
                  </div>
                </div>
              )}

              {lastPaid && (
                <div className="bil-mini-row">
                  <span><i className="fa-solid fa-receipt" /> Last payment</span>
                  <b>{fmtNaira(lastPaid.payAmount)} · {fmtDate(lastPaid.payCreatedAt)}</b>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bil-hero-empty">
            <span className="bil-hero-eyebrow"><i className="fa-solid fa-gem" /> No active plan</span>
            <h2>Choose a plan to get started</h2>
            <p>Pick any plan below — you can switch or upgrade at any time.</p>
          </div>
        )}
      </section>

      {/* Plans */}
      <section className="bil-section" ref={plansRef}>
        <div className="bil-section-head">
          <div>
            <h3>Plans</h3>
            <p>Prices in naira. Switch whenever you like — changes apply right after checkout.</p>
          </div>
        </div>

        <div className="bil-plans">
          {plans.map(plan => {
            const price     = Number(plan.plPrice) || 0;
            const isCurrent = current && current.plSlug === plan.plSlug;
            const isFree    = price <= 0;
            const isRec     = plan.plSlug === recommended && !isCurrent;
            const disabled  = isCurrent || upgrading === plan.plSlug || (!isFree && !pocketfiReady);
            const label     = isCurrent ? 'Current plan'
              : isFree ? 'Switch to Free'
              : current && price < currentPrice ? `Switch to ${plan.plName}`
              : `Upgrade to ${plan.plName}`;
            return (
              <article key={plan.plSlug} className={`bil-plan ${isCurrent ? 'is-current' : ''} ${isRec ? 'is-rec' : ''}`}>
                <div className="bil-plan-top">
                  <h4>{plan.plName}</h4>
                  {isCurrent && <span className="bil-tag tag-current"><i className="fa-solid fa-check" /> Current</span>}
                  {isRec && <span className="bil-tag tag-rec"><i className="fa-solid fa-star" /> Recommended</span>}
                </div>
                {plan.plDescription && <p className="bil-plan-desc">{plan.plDescription}</p>}
                <div className="bil-plan-price">
                  {isFree ? 'Free' : fmtNaira(price)}
                  {!isFree && plan.plInterval && <span>/ {plan.plInterval} days</span>}
                </div>
                {plan.plMaxBots != null && (
                  <div className="bil-plan-bots"><i className="fa-solid fa-robot" /> {Number(plan.plMaxBots) >= 999 ? 'Unlimited' : plan.plMaxBots} bot{Number(plan.plMaxBots) === 1 ? '' : 's'}</div>
                )}
                <button
                  type="button"
                  className={`bil-btn bil-btn-block ${isCurrent ? 'bil-btn-muted' : isRec ? 'bil-btn-brand' : 'bil-btn-dark'}`}
                  disabled={disabled}
                  onClick={() => handleUpgrade(plan)}
                  title={!isFree && !pocketfiReady ? 'Online payments not configured yet' : ''}
                >
                  {upgrading === plan.plSlug
                    ? <><span className={`bil-spin ${isRec ? '' : 'light'}`} /> Redirecting…</>
                    : <>{label}{!isCurrent && <i className="fa-solid fa-arrow-right" />}</>}
                </button>
                {plan.plFeatures?.length > 0 && (
                  <ul className="bil-plan-feats">
                    {plan.plFeatures.map((f, i) => <li key={i}><i className="fa-solid fa-check" /> {f}</li>)}
                  </ul>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {/* History */}
      <section className="bil-section" ref={historyRef}>
        <div className="bil-section-head">
          <div>
            <h3>Payment history</h3>
            <p>{totalTx ? `${totalTx} transaction${totalTx === 1 ? '' : 's'}` : 'Your payments will appear here.'}</p>
          </div>
          {totalPaid > 0 && (
            <div className="bil-total">
              <span>Total paid</span>
              <b>{fmtNaira(totalPaid)}</b>
            </div>
          )}
        </div>

        <div className="bil-table-card">
          {history.length === 0 ? (
            <div className="bil-empty">
              <span className="bil-empty-ic"><i className="fa-solid fa-receipt" /></span>
              <b>No payments yet</b>
              <span>When you upgrade or renew a plan, the receipt shows up here.</span>
            </div>
          ) : (
            <>
              <div className="bil-table" role="table" aria-label="Payment history">
                <div className="bil-tr bil-th" role="row">
                  <span role="columnheader">Plan</span>
                  <span role="columnheader">Date</span>
                  <span role="columnheader">Reference</span>
                  <span role="columnheader" className="ta-r">Amount</span>
                  <span role="columnheader" className="ta-r">Status</span>
                </div>
                {paginated.map(p => (
                  <div key={p.payId} className="bil-tr" role="row">
                    <span role="cell" className="bil-td-plan">
                      <span className={`bil-tx-ic ${STATUS_META[p.payStatus]?.cls || 'is-pending'}`}>
                        <i className={`fa-solid ${p.payStatus === 'success' ? 'fa-arrow-up' : p.payStatus === 'failed' ? 'fa-xmark' : p.payStatus === 'cancelled' ? 'fa-ban' : 'fa-clock'}`} />
                      </span>
                      <span>
                        <b>{p.plName}</b>
                        <em className="bil-td-mobile-date">{fmtDate(p.payCreatedAt)}</em>
                      </span>
                    </span>
                    <span role="cell" className="bil-td-date">{fmtDate(p.payCreatedAt, true)}</span>
                    <span role="cell" className="bil-td-ref">
                      {p.payPocketfiId
                        ? <button type="button" title="Copy full reference" onClick={() => navigator.clipboard?.writeText(p.payPocketfiId).then(() => toast.success('Reference copied')).catch(() => {})}>
                            …{p.payPocketfiId.slice(-8)} <i className="fa-regular fa-copy" />
                          </button>
                        : '—'}
                    </span>
                    <span role="cell" className="bil-td-amt ta-r">{fmtNaira(p.payAmount)}</span>
                    <span role="cell" className="bil-td-status ta-r"><StatusPill status={p.payStatus} /></span>
                  </div>
                ))}
              </div>
              <Pagination page={page} total={totalTx} pageSize={PAGE_SIZE} onChange={changePage} />
            </>
          )}
        </div>
      </section>

      <p className="bil-foot-note">
        <i className="fa-solid fa-lock" /> Payments are processed securely by PocketFi. Questions about a charge? Reach us from the Contact page.
      </p>
    </div>
  );
}
